import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/lib/csvParser.js
//     parseCSV reads files via FileReader.readAsText(file, 'utf-8') ONLY.
//     No size limit anywhere. No Windows-1252 support.
//     normaliseDate handles only ISO (YYYY-MM-DD) and DD/MM/YYYY formats
//     in the default path. US dates need the custom mapper's dateFormat=MDY.
//   - src/components/CsvColumnMapper.js
//     Split-amount mode IS supported (amountMode: 'single' | 'split').
//     dateFormat selector: auto / DMY / MDY / YMD.
//     reverseSign checkbox for credit-card statements.
//   - src/pages/NewRequest.js
//     No "Adjust columns" button. Mapper appears either when auto-detect
//     extracts zero transactions (with a toast saying so) or when the user
//     picks "Custom mapping" from the software dropdown before upload.
// Removed:
//   - "10 MB CSV upload cap" -- no cap is enforced in code. The effective
//     limit is browser memory (very large files may freeze the browser),
//     but no specific MB number is correct.
//   - "Receipt Chaser only reads one column for amount; combine debits and
//     credits before importing" -- false. The custom mapper supports
//     split money-in/money-out columns via the amount mode toggle.
//   - "Supported encodings: UTF-8 and Windows-1252" -- only UTF-8.
//   - "Adjust columns" button -- no such button. Replaced with accurate
//     description of how to reach the mapper.
//   - "Receipt Chaser defaults to UK format. If US-formatted, change the
//     format on the column mapping screen" -- implies the format selector
//     is always available; it's only in the custom mapper, not the
//     default auto-detect path.
// Pass 2 added:
//   - The toast message users actually see when auto-detect fails
//   - How to deliberately invoke the custom mapper (software dropdown =
//     "Custom mapping" before upload)
//   - Reverse-sign option for credit card statements

export default {
  title:   "CSV import isn't working",
  summary: 'Common CSV problems, when to use the custom mapper, encoding issues, and how to fix date and amount errors.',
  body: (
    <>
      <P>
        CSV import problems are almost always about one of four things:
        the file's format, the column structure, the date format, or
        character encoding. Work through this article in order and
        you'll usually find the cause in the first two checks.
      </P>

      <Callout variant="tip" title="Quickest first check">
        Open the CSV in a plain text editor (Notepad on Windows,
        TextEdit on Mac, or VS Code on either). If you can't see neat
        columns separated by commas with a header row at the top, the
        file isn't a usable CSV and that's your problem.
      </Callout>

      <H>"Couldn't read this file automatically"</H>
      <P>
        If you upload a CSV and Receipt Chaser shows a toast saying
        "Couldn't read this file automatically. Map the columns
        yourself below.", auto-detect didn't recognise the format.
        This is normal for bank exports, credit card statements, and
        any CSV that isn't from Xero, Sage, QuickBooks, or FreeAgent.
        The column mapper opens automatically below the upload box.
      </P>
      <P>
        On the mapper, pick which column holds the date, which holds
        the description, and which holds the amount. The live
        preview at the bottom updates as you change settings. Click{' '}
        <Code>Confirm mapping</Code> when the preview looks right.
      </P>

      <Callout variant="info" title="Deliberately using the custom mapper">
        If you know upfront your file isn't from a supported accounting
        package, change the dropdown to{' '}
        <Code>🛠 Custom mapping</Code> before uploading. The mapper
        opens immediately and you skip the auto-detect step entirely.
      </Callout>

      <H>The file uploads but no transactions appear</H>
      <P>
        If the auto-detect succeeded but the list is empty or
        suspiciously short, common causes:
      </P>
      <UL>
        <Li>
          <strong>The wrong rows were treated as headers.</strong>{' '}
          Some bank CSVs have metadata rows above the column headers
          (account number, statement period, etc.). The auto-detect
          may have picked the wrong row as headers. Switch to the
          custom mapper and use its header-row picker to point at
          the correct row.
        </Li>
        <Li>
          <strong>Multiple rows merged into one cell.</strong> A
          symptom of a wrong delimiter. UK CSVs sometimes use
          semicolons instead of commas. Open the file in Excel using
          the <Code>Data → Get Data → From Text/CSV</Code> import
          option, which lets you pick the right delimiter, then save
          as CSV (comma-delimited, UTF-8).
        </Li>
        <Li>
          <strong>The amount column has empty cells.</strong> If your
          export has separate "money in" and "money out" columns and
          most rows have one or the other empty, use the custom
          mapper and switch the amount mode to{' '}
          <em>Split money-in / money-out</em>. Receipt Chaser handles
          split columns natively. You don't need to combine them in
          Excel first.
        </Li>
      </UL>

      <H>Dates are wrong or missing</H>
      <P>
        The most common date issue is US versus UK format. A row
        dated <Code>04/05/2026</Code> means 4 May in the UK and 5
        April in the US. Receipt Chaser's default date handling
        interprets all <Code>DD/MM/YYYY</Code> strings as UK format.
        If your export is US-formatted (most QuickBooks Online
        exports are, regardless of country), use the custom mapper
        and set the date format dropdown to <Code>MM/DD/YYYY (US)</Code>.
      </P>
      <P>
        Other date issues:
      </P>
      <UL>
        <Li>
          <strong>Dates as plain numbers.</strong> If dates look like
          {' '}<Code>45200</Code> or similar, that's Excel storing
          them as serial numbers. Open the file in Excel, select the
          date column, and format as a proper date before re-saving.
        </Li>
        <Li>
          <strong>Mixed date formats in the same column.</strong>{' '}
          This happens when a CSV has been opened in Excel without
          specifying the date format on import. Excel sometimes
          interprets the first few rows one way and later rows
          another. The fix is to re-import in Excel using{' '}
          <Code>Data → Get Data → From Text/CSV</Code> and explicitly
          set the date format on the column.
        </Li>
      </UL>

      <H>The amount column is being read wrong</H>
      <UL>
        <Li>
          <strong>Amounts include currency symbols.</strong> The
          parser handles £ and commas. If you see odd numbers anyway,
          find-and-replace the symbols in Excel to be safe before
          uploading.
        </Li>
        <Li>
          <strong>Amounts with brackets around negatives.</strong>{' '}
          Some exports use <Code>(£45.20)</Code> instead of{' '}
          <Code>-£45.20</Code>. Excel treats these as text and the
          parser won't recognise them as negative numbers.
          Find-and-replace the brackets with a minus sign before
          uploading.
        </Li>
        <Li>
          <strong>Credit card expenses showing as positive.</strong>{' '}
          Credit card statements often show spending as positive
          numbers. In the custom mapper, tick{' '}
          <em>Reverse sign of all amounts</em> to flip them.
          Expenditure will then read as negative as expected.
        </Li>
      </UL>

      <H>Strange characters in descriptions</H>
      <P>
        If descriptions display as <Code>caf?</Code> or{' '}
        <Code>Café</Code> (literal "C-a-f-Ã-©") instead of{' '}
        <em>café</em>, the file's character encoding isn't UTF-8.
        Receipt Chaser reads CSVs as UTF-8 only. The fix:
      </P>
      <OL>
        <Li>Open the file in Excel.</Li>
        <Li>
          Use <Code>File → Save As</Code>, pick{' '}
          <Code>CSV UTF-8 (Comma delimited)</Code> as the format.
        </Li>
        <Li>Re-upload.</Li>
      </OL>

      <H>Very large files</H>
      <P>
        Receipt Chaser doesn't enforce a CSV size limit. In practice,
        files with tens of thousands of rows will work, but very
        large files (say 50,000 rows and up) can slow the browser
        or fail silently if memory runs out. A receipt request that
        large is unusual anyway: split the file by date range in
        your accounting software and send several smaller requests
        rather than one giant one.
      </P>

      <H>"My accounting software's export just won't work"</H>
      <P>
        If you've ruled out everything above and your accounting
        software's export simply isn't accepted, two backup options:
      </P>
      <OL>
        <Li>
          <strong>Re-save through Excel as UTF-8 CSV.</strong> Open
          the export, then save it as <Code>CSV UTF-8</Code>. This
          fixes most encoding and delimiter issues in one step.
        </Li>
        <Li>
          <strong>Send us a copy.</strong> If you've tried the above
          and it still won't work, send us the file (with any
          sensitive info redacted) via the{' '}
          <Link to="/contact">Contact</Link> page and we'll take a
          look. Your file may have a quirk we can teach the importer
          to handle.
        </Li>
      </OL>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/importing-transactions">Importing transactions from your accounting software</Link>
        </Li>
        <Li>
          <Link to="/support/sending-your-first-request">Sending your first receipt request</Link>
        </Li>
      </UL>
    </>
  ),
}
